import { motion } from "framer-motion";
import { ArrowUpRight, WashingMachine, Shirt, Truck } from "lucide-react";
import { FadeUp } from "./Reveal";
import { BOOKING_LINKS } from "../config";

const chapters = [
  {
    number: "01",
    title: "Self-Service",
    heading: "Our floor, your schedule.",
    body: "Step into a bright, spotless space lined with modern, high-capacity machines. Bring a book, bring the kids, bring the whole week's worth — our washers and dryers make quick work of it while you make yourself at home.",
    icon: WashingMachine,
    img: "/assets/folding-warm.png",
    alt: "A mother and daughter folding warm, fresh laundry together in the laundromat",
    cta: "Plan Your Visit",
    href: "#contact",
    testid: "service-self-service-cta",
  },
  {
    number: "02",
    title: "Wash, Dry & Fold",
    heading: "You drop it off. We do the rest.",
    body: "Hand us the hamper and reclaim your day. Every load is washed with care, dried just right, and folded into crisp, ready-to-put-away bundles — separated, fluffed, and treated like it's our own.",
    icon: Shirt,
    img: "/assets/staff-fold.png",
    alt: "A Spintastic City team member in branded tee and ball cap folding laundry at a table",
  },
  {
    number: "03",
    title: "Pickup & Delivery",
    heading: "Clean laundry, at your door.",
    body: "Our own Spintastic van — backed by trusted gig drivers — collects your laundry and brings it back fresh, folded, and ready. Schedule in seconds and track it every step of the way, right from your phone.",
    icon: Truck,
    img: "/assets/van-branded.png",
    alt: "Spintastic City delivery van wrapped in brand colors with the company logo",
    cta: "Schedule a Pickup",
    href: BOOKING_LINKS.pickupDelivery,
    testid: "service-pickup-cta",
  },
];

const Chapter = ({ chapter, index }) => {
  const flipped = index % 2 === 1;
  const Icon = chapter.icon;

  return (
    <div
      className="relative grid items-center gap-12 lg:grid-cols-12 lg:gap-20"
      data-testid={`service-chapter-${chapter.number}`}
    >
      <span
        aria-hidden="true"
        className="pointer-events-none absolute -top-16 select-none font-serif text-[9rem] font-bold leading-none text-[#E2DFD8]/60 sm:text-[12rem] lg:-top-24"
        style={{ [flipped ? "right" : "left"]: 0 }}
      >
        {chapter.number}
      </span>

      <div className={`relative lg:col-span-6 ${flipped ? "lg:order-2" : ""}`}>
        <FadeUp>
          <div className="inline-flex items-center gap-3 rounded-full border border-[#E2DFD8] bg-white/60 px-4 py-2">
            <Icon className="h-4 w-4 text-[#C85C40]" />
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-[#5C5C5A]">
              {chapter.title}
            </span>
          </div>
          <h3 className="mt-6 font-serif text-4xl tracking-tight sm:text-5xl">
            {chapter.heading}
          </h3>
          <p className="mt-6 max-w-lg text-base leading-relaxed text-[#5C5C5A] md:text-lg">
            {chapter.body}
          </p>
          {chapter.cta && (
            <a
              href={chapter.href}
              {...(chapter.href.startsWith("http")
                ? { target: "_blank", rel: "noopener noreferrer" }
                : {})}
              data-testid={chapter.testid}
              className="group mt-8 inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] px-7 py-3.5 text-sm font-bold text-[#F9F6F0] transition-[background-color,transform] duration-300 hover:scale-[0.98] hover:bg-[#C85C40]"
            >
              {chapter.cta}
              <ArrowUpRight className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          )}
        </FadeUp>
      </div>

      <div className={`lg:col-span-6 ${flipped ? "lg:order-1" : ""}`}>
        <FadeUp delay={0.15}>
          <div className="overflow-hidden rounded-[2rem] shadow-[0_4px_24px_rgba(0,0,0,0.04)]">
            <motion.img
              src={chapter.img}
              alt={chapter.alt}
              initial={{ scale: 1.15 }}
              whileInView={{ scale: 1 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
              className="aspect-[4/3] w-full object-cover will-change-transform"
            />
          </div>
        </FadeUp>
      </div>
    </div>
  );
};

export default function Services() {
  return (
    <section
      id="services"
      data-testid="services-section"
      className="mx-auto max-w-7xl scroll-mt-28 px-6 py-32 lg:px-10 lg:py-40"
    >
      <FadeUp className="mb-24 max-w-2xl lg:mb-32">
        <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#C85C40]">
          What we do
        </p>
        <h2 className="mt-4 font-serif text-4xl leading-none tracking-tight sm:text-5xl lg:text-6xl">
          Three ways to{" "}
          <span className="italic text-[#C85C40]">love</span> laundry day.
        </h2>
      </FadeUp>

      <div className="flex flex-col gap-32 lg:gap-40">
        {chapters.map((c, i) => (
          <Chapter key={c.number} chapter={c} index={i} />
        ))}
      </div>
    </section>
  );
}
